import type { MandantEntitlements } from '@Hooks/Permissions';
import { ApiUserProfile } from '@Model';
import { isNullOrEmpty } from '@synqo/frontend-component-library';
import { createContext, useCallback, useContext } from 'react';

export type AppState = Record<string, string>;

export interface AppStatePayload {
    current: AppState;
    profile: ApiUserProfile;
    // Entitlements are given if they have a non null and non empty value.
    // We get them based on Entitlement name, not code!
    entitlements: MandantEntitlements;
    set: (key: string, value: string) => void;
    reload: () => void;
}

export const emptyProfile: ApiUserProfile = {
    id: '',
    email: '',
    name: '',
    client_legal_document_accepted: false,
    user_legal_document_accepted: false,
    permissions: [],
    uses_external_erp: false,
};

export const AppStateContext = createContext<AppStatePayload>({
    current: {},
    profile: emptyProfile,
    entitlements: {},
    set: () => {},
    reload: () => {},
});

export function useAppState() {
    const state = useContext(AppStateContext);

    return state;
}

export function useHasPermission() {
    const {
        profile: { permissions },
    } = useAppState();

    return useCallback((permission: string) => permissions.includes(permission), [permissions]);
}

/**
 * Whether the current mandant uses an external ERP. When false (the default), Synqo is the sole
 * backend. Used to gate external-ERP affordances such as master-data imports.
 */
export function useUsesExternalErp(): boolean {
    const { profile } = useAppState();
    return profile.uses_external_erp;
}

/**
 * Whether the current mandant has the Portal entitlement enabled.
 */
export function usePortalEnabled(): boolean {
    const { entitlements } = useAppState();
    return !isNullOrEmpty(entitlements['portal']);
}

export function useProfileName() {
    const { profile } = useAppState();

    return profile.name;
}

export function useProfileId() {
    const { profile } = useAppState();

    return profile.id;
}
